import React, { useMemo, useState } from 'react';
import { Alert, Box, Button, CircularProgress, Stack, Typography } from '@mui/material';
import ImageOutlinedIcon from '@mui/icons-material/ImageOutlined';

import type { LibraryImportResult, LibraryItem } from '../../domain';
import { useImportFromLattice, useLibrary } from '../../queries';
import { libraryErrorMessage } from '../../services';
import { LibraryCard } from './LibraryCard';
import { mergeLibraryItems, type LibrarySource } from './libraryUtils';

interface Props {
  /** 'lattice' — Lattice work only; 'all' — Coral and Lattice, newest first. */
  source: Exclude<LibrarySource, 'coral'>;
  onImported: (result: LibraryImportResult) => void;
  onImportError: (message: string) => void;
}

/**
 * Right pane for the shared SmartAI library. Lists one or both products'
 * halves, pages them by `hasMore`, and offers "Use in Coral" on Lattice images
 * and video. A Lattice failure is a notice above the grid, never a reason to
 * hide the Coral half.
 */
export const LibraryGrid: React.FC<Props> = ({ source, onImported, onImportError }) => {
  const showCoral = source === 'all';
  const coral = useLibrary('coral', { enabled: showCoral });
  const lattice = useLibrary('lattice');
  const importItem = useImportFromLattice();
  const [importingId, setImportingId] = useState<string | null>(null);

  const items = useMemo(
    () =>
      mergeLibraryItems(
        showCoral ? (coral.data?.pages.flatMap(p => p.items) ?? []) : [],
        lattice.data?.pages.flatMap(p => p.items) ?? [],
      ),
    [showCoral, coral.data, lattice.data],
  );

  const handleImport = (item: LibraryItem) => {
    setImportingId(item.id);
    importItem.mutate(item, {
      onSuccess: onImported,
      onError: e => onImportError(libraryErrorMessage(e, 'Could not add this to Coral.')),
      onSettled: () => setImportingId(null),
    });
  };

  const isLoading = lattice.isLoading || (showCoral && coral.isLoading);
  const hasMore = !!lattice.hasNextPage || (showCoral && !!coral.hasNextPage);
  const isFetchingMore = lattice.isFetchingNextPage || coral.isFetchingNextPage;
  const loadMore = () => {
    if (lattice.hasNextPage && !lattice.isFetchingNextPage) lattice.fetchNextPage();
    if (showCoral && coral.hasNextPage && !coral.isFetchingNextPage) coral.fetchNextPage();
  };

  const title = source === 'all' ? 'All SmartAI work' : 'Lattice';
  const subtitle =
    source === 'all' ? 'Coral Creator and Lattice, newest first' : 'Your work in Coral Lattice';

  return (
    <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', minHeight: 0 }}>
      <Stack
        direction="row"
        spacing={1}
        alignItems="baseline"
        sx={{ px: 3, py: 1.5, borderBottom: theme => `1px solid ${theme.palette.divider}` }}
      >
        <Typography variant="h6" fontWeight={600}>
          {title}
        </Typography>
        <Typography variant="caption" color="text.secondary">
          {subtitle}
          {items.length > 0 &&
            ` · ${items.length}${hasMore ? '+' : ''} item${items.length === 1 ? '' : 's'}`}
        </Typography>
      </Stack>

      <Box sx={{ flex: 1, overflowY: 'auto', p: 3 }}>
        {lattice.error && (
          <Alert severity="warning" sx={{ mb: 2 }}>
            {`Couldn't load your Lattice work: ${libraryErrorMessage(lattice.error, 'Lattice is unreachable.')}`}
          </Alert>
        )}
        {showCoral && coral.error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {`Couldn't load your Coral work: ${libraryErrorMessage(coral.error, 'Failed to load assets.')}`}
          </Alert>
        )}

        {isLoading && (
          <Stack alignItems="center" sx={{ py: 6 }}>
            <CircularProgress size={28} />
          </Stack>
        )}

        {!isLoading && items.length === 0 && (
          <Stack alignItems="center" spacing={1} sx={{ py: 6 }}>
            <ImageOutlinedIcon sx={{ fontSize: 56, opacity: 0.3 }} />
            <Typography variant="body2" color="text.secondary">
              {source === 'all' ? 'Nothing here yet.' : 'No Lattice work yet.'}
            </Typography>
          </Stack>
        )}

        {items.length > 0 && (
          <Box
            sx={{
              display: 'grid',
              gridTemplateColumns: 'repeat(auto-fill, minmax(180px, 1fr))',
              gap: 2,
            }}
          >
            {items.map(item => (
              <LibraryCard
                key={`${item.product}:${item.id}`}
                item={item}
                onImport={handleImport}
                importing={importingId === item.id}
                importDisabled={importingId !== null}
              />
            ))}
          </Box>
        )}

        {hasMore && (
          <Stack alignItems="center" sx={{ mt: 3 }}>
            <Button
              variant="outlined"
              size="small"
              onClick={loadMore}
              disabled={isFetchingMore}
              startIcon={isFetchingMore ? <CircularProgress size={14} /> : undefined}
            >
              {isFetchingMore ? 'Loading…' : 'Load more'}
            </Button>
          </Stack>
        )}
      </Box>
    </Box>
  );
};
